import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import "./Dashboard.css";
import logo1 from "../Assets/project-plan.png";
import logo2 from "../Assets/clients.png";
import logo3 from "../Assets/award.png";
import logo4 from "../Assets/team.png";
import heroImg from "../Assets/Dashboard_Hero.jpg";
import blogImg from "../Assets/Dashboard_Blog.jpeg";

const Dashboard = () => {
  const achievementsRef = useRef(null);

  const testimonials = [
    {
      id: 1,
      name: "Rutland Webdesigner",
      company: "Web Design Agency",
      logo: "/rutland-webdesigner.png",
    },
    {
      id: 2,
      name: "Dr. Nirav Desai",
      company: "Healthcare Professional",
      logo: "/dr-nirav-desai.png",
    },
    {
      id: 3,
      name: "MusicZed",
      company: "Music Platform",
      logo: "/music-zed.png",
    },
    {
      id: 4,
      name: "Aeiforia Tech",
      company: "Tech Company",
      logo: "/aeiforia-tech.png",
    },
  ];

  useEffect(() => {
    const counters = achievementsRef.current.querySelectorAll(
      ".dashboard-achievements-counter",
    );
    let hasAnimated = false;

    const animateCounters = () => {
      counters.forEach((counter) => {
        const target = +counter.getAttribute("data-target");
        const duration = 2000;
        const stepTime = 20;
        const increment = target / (duration / stepTime);
        let count = 0;

        const updateCounter = () => {
          count += increment;
          if (count < target) {
            counter.textContent = Math.ceil(count) + "+";
            setTimeout(updateCounter, stepTime);
          } else {
            counter.textContent = target + "+";
          }
        };
        updateCounter();
      });
    };

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasAnimated) {
          animateCounters();
          hasAnimated = true;
        }
      },
      { threshold: 0.4 },
    );

    observer.observe(achievementsRef.current);

    const allElements = document.querySelectorAll(
      ".dashboard section:not(.dashboard-hero-section)",
    );

    const scrollObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("scroll-show");
            scrollObserver.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2 },
    );

    allElements.forEach((el) => {
      el.classList.add("scroll-hidden");
      scrollObserver.observe(el);
    });

    return () => {
      observer.disconnect();
      scrollObserver.disconnect();
    };
  }, []);

  return (
    <div className="dashboard">
      {/* Hero Section */}
      <section className="dashboard-hero-section">
        <div className="dashboard-hero-text">
          <h1>Bringing Your Ideas to Reality with Digital Solutions</h1>
          <p>
            You have the idea to disrupt an industry and we believe in
            empowering our clients with an ambitious mission.
          </p>
          <Link to="/contact" className="btn">
            Let's Get Started
          </Link>
        </div>

        <div className="dashboard-hero-image">
          <img src={heroImg} alt="Hero" />
        </div>
      </section>

      {/* Services Section */}
      <section className="dashboard-services-section">
        <h1>Services</h1>
        <h2>
          Reimagine digital products, from concept to launch with our expertise
        </h2>
        <p>
          We are a small team with a big heart and believe we can bring your
          idea to life through scalable and sustainable design solutions.
        </p>

        <div className="dashboard-services-grid">
          <div className="dashboard-services-card">
            <h3>Digital Marketing</h3>
            <p>Facebook Post/Promotion</p>
            <p>Instagram Post/Promotion</p>
            <p>Social Media Posts - Design</p>
            <p>Keyword Ranking</p>
            <p>SEO & Ranking</p>
          </div>
          <div className="dashboard-services-card">
            <h3>Research</h3>
            <p>Heuristic Analysis</p>
            <p>Design Audit</p>
            <p>Competitive Analysis</p>
            <p>UX Research</p>
            <p>Usability Testing</p>
          </div>
          <div className="dashboard-services-card">
            <h3>Design</h3>
            <p>User Experience Design</p>
            <p>User Interface Design</p>
            <p>Digital Prototyping</p>
            <p>System Design</p>
            <p>Interaction Design</p>
          </div>
          <div className="dashboard-services-card">
            <h3>Development</h3>
            <p>Front-end Development</p>
            <p>Mobile Applications</p>
            <p>Web Applications</p>
            <p>Custom Applications</p>
            <p>No-code Applications</p>
          </div>
        </div>
      </section>

      {/* Achievements Section */}
      <section className="dashboard-achievements-section" ref={achievementsRef}>
        <h1>Our Achievements</h1>
        <h3>
          From early-stage startups to renowned brands, we have plenty of
          stories to share.
        </h3>

        <div className="dashboard-achievements-container">
          <div className="dashboard-achievement-card">
            <img src={logo1} alt="completed projects" />
            <p>Projects Completed</p>
            <span className="dashboard-achievements-counter" data-target="150">
              0+
            </span>
          </div>

          <div className="dashboard-achievement-card">
            <img src={logo2} alt="satisfied clients" />
            <p>Satisfied Clients</p>
            <span className="dashboard-achievements-counter" data-target="120">
              0+
            </span>
          </div>

          <div className="dashboard-achievement-card">
            <img src={logo3} alt="awards" />
            <p>Awards Won</p>
            <span className="dashboard-achievements-counter" data-target="10">
              0+
            </span>
          </div>

          <div className="dashboard-achievement-card">
            <img src={logo4} alt="members" />
            <p>Team Members</p>
            <span className="dashboard-achievements-counter" data-target="12">
              0+
            </span>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="dashboard-testimonials-section">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="dashboard-testimonials-header"
        >
          <h1>Our Trusted Clients</h1>
          <h3>Trusted by businesses across various industries</h3>
        </motion.div>

        <div className="dashboard-testimonials-grid">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={testimonial.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={{
                scale: 1.05,
                boxShadow: "0 8px 25px rgba(0, 0, 0, 0.15)",
              }}
              className="dashboard-testimonial-card"
            >
              <div className="dashboard-testimonial-logo">
                <img
                  src={testimonial.logo}
                  alt={`${testimonial.name} logo`}
                  className="dashboard-testimonial-img"
                />
              </div>
              <div className="dashboard-testimonial-content">
                <h4>{testimonial.name}</h4>
                <p>{testimonial.company}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Projects Section */}
      <section className="dashboard-projects-section">
        <div className="dashboard-project-card">
          <h1>Checkout our recent projects</h1>
          <h3>
            From mobile apps to web apps, we make businesses come alive across
            the globe for multiple domains.
          </h3>
          <Link to="/portfolio" className="btn">
            Explore our projects
          </Link>
        </div>
      </section>

      {/* Blog Section */}
      <section className="dashboard-blog-section">
        <div className="dashboard-blog-card">
          <div className="dashboard-blog-image">
            <img src={blogImg} alt="Blog Thumbnail" />
          </div>
          <div className="dashboard-blog-content">
            <span className="dashboard-blog-tag">Blog</span>
            <h2>
              Comparing the Performance of a Mobile App built in Native platform
              and cross-platform
            </h2>
            <p className="dashboard-blog-author">
              By Bhaumik Prajapati on 03 May, 2021
            </p>
            <Link to="/blog" className="dashboard-blog-link">
              View Blog
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="dashboard-cta-section">
        <h3>Have an idea?</h3>
        <h1>Let's build together and make it a reality.</h1>
        <Link to="/contact" className="btn">
          Get in Touch
        </Link>
      </section>
    </div>
  );
};

export default Dashboard;
